import PropTypes from 'prop-types';
import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { bindAll } from '@bibliocommons/utils-react';
import FullScreenOverlay from '@bibliocommons/deprecated-base-full-screen-overlay';
import BibBrief from 'app/components/shared/BibBrief';
import { PrimaryButton, TextButton } from '@bibliocommons/deprecated-base-buttons';
import JacketCover from '@bibliocommons/shared-jacket-cover';
import AccessibleTextArea from '@bibliocommons/deprecated-base-accessibility-text-area';
import { FormattedMessage } from 'react-intl';
import { AlertFailure } from '@bibliocommons/deprecated-base-alerts';
import FormatInfoContainer from 'app/components/shared/FormatInfo/FormatInfoContainer';

import './GroupSearchFeedback.scss';

class GroupSearchFeedback extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
    this.state = {
      notes: ''
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.isEditing !== this.props.isEditing && this.props.sourceBib && !this.hasMatches()) {
      this.props.groupSearchFeedbackActions.updateShouldMatch(true);
    }
  }

  handleShouldMatchChange(e) {
    const shouldMatch = e.currentTarget.value === 'true';
    this.props.groupSearchFeedbackActions.updateShouldMatch(shouldMatch);
  }

  handleSubmit(e) {
    e.preventDefault();
    if (!this.props.isSubmitting) {
      this.props.groupSearchFeedbackActions.submitFeedback({
        messagingId: this.props.sourceBib.get('id'),
        notes: this.state.notes
      });
    }
    this.setState({ notes: '' });
  }

  handleSelectMatchingBib(bib) {
    this.props.groupSearchFeedbackActions.selectMatchingBib(bib);
  }

  handleMessageChange(notes) {
    this.setState({ notes });
  }

  hasMatches() {
    return !this.getCandidateItems().isEmpty();
  }

  getCandidateItems() {
    return this.props.searchResults
      .filter(group => !group.manifestations.includes(this.props.sourceBib))
      .map(group => group.representative);
  }

  renderMatchingBibSelect() {
    if (this.props.shouldMatch) {
      if (this.props.bibToMatch) {
        return (
          <div>
            <h4>It should be grouped with...</h4>
            <div className="matching-bib-list">
              <div className="search-result-bib">
                <div className="bib-jacket">
                  <JacketCover
                    src={this.props.bibToMatch.getIn(['briefInfo', 'jacket'])}
                    format={this.props.bibToMatch.getIn(['briefInfo', 'format'])}
                  />
                </div>
                <div className="group-bib-brief-info">
                  <BibBrief bib={this.props.bibToMatch.get('briefInfo')} />
                  <FormatInfoContainer item={this.props.bibToMatch} />
                  <TextButton
                    className="change-matching-bib"
                    handleClick={this.props.groupSearchFeedbackActions.changeMatchingBib}
                  >
                    Pick a different item to group with
                  </TextButton>
                </div>
              </div>
            </div>
          </div>
        );
      }

      const items = this.getCandidateItems();
      return (
        <div className="select-matching-bib">
          <h4>Select the item that this should be grouped with</h4>
          <div className="matching-bib-list">
            {items.map(item => (
              <div className="search-result-bib" key={item.get('id')}>
                <div className="bib-jacket">
                  <JacketCover src={item.getIn(['briefInfo', 'jacket'])} format={item.getIn(['briefInfo', 'format'])} />
                </div>
                <div className="group-bib-brief-info">
                  <BibBrief bib={item.get('briefInfo')} />
                  <FormatInfoContainer item={item} />
                  <PrimaryButton
                    handleClick={() => this.handleSelectMatchingBib(item)}
                    isLoading={this.props.isSubmitting}
                  >
                    Group with this item
                  </PrimaryButton>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return null;
  }

  renderShouldMatchOptions() {
    return (
      <fieldset className="should-match">
        <legend className="should-match-label">This item should be...</legend>
        <div className="radio">
          <label htmlFor="diff-group">
            <input
              type="radio"
              name="shouldMatch"
              value="true"
              id="diff-group"
              onChange={this.handleShouldMatchChange}
              disabled={!this.hasMatches()}
              required
            />
            in a different group
          </label>
        </div>

        <div className="radio">
          <label htmlFor="no-group">
            <input
              type="radio"
              name="shouldMatch"
              value="false"
              id="no-group"
              onChange={this.handleShouldMatchChange}
              required
            />
            not be grouped
          </label>
        </div>
      </fieldset>
    );
  }

  renderOverlayContents() {
    const { sourceBib, isMissingRequiredFields } = this.props;

    if (!sourceBib) {
      return null;
    }

    const sourceBibBrief = sourceBib.get('briefInfo');

    return (
      <div className="cp-group-search-feedback">
        <h2>Report incorrect grouping for...</h2>

        <div className="clearfix source-bib-display">
          <div className="source-bib-jacket-cover">
            <JacketCover
              src={sourceBibBrief.get('jacket')}
              format={sourceBibBrief.get('format')}
              alt={sourceBibBrief.get('title')}
            />
          </div>
          <div className="source-bib-info">
            <BibBrief bib={sourceBibBrief} titleTag="h3" />
            <FormatInfoContainer item={sourceBib} />
          </div>
        </div>

        <form onSubmit={this.handleSubmit}>
          {this.renderShouldMatchOptions()}
          {this.renderMatchingBibSelect()}
          {isMissingRequiredFields ? (
            <AlertFailure>
              {' '}
              <FormattedMessage id="groupSearchFeedbackFormError" />{' '}
            </AlertFailure>
          ) : null}
          <label htmlFor="additional-comments" id="additional-comments-label">
            Additional comments
          </label>
          <AccessibleTextArea
            maxLength={4000}
            id="additional-comments"
            labelledById="additional-comments-label"
            value={this.state.notes}
            handleChange={this.handleMessageChange}
            errorKey="additional-comments"
            rows="5"
            cols="60"
          />

          <PrimaryButton type="submit" isLoading={this.props.isSubmitting}>
            Submit
          </PrimaryButton>
        </form>
      </div>
    );
  }

  render() {
    const {
      isEditing,
      groupSearchFeedbackActions: { closeOverlay }
    } = this.props;
    return (
      <FullScreenOverlay open={isEditing} handleClose={closeOverlay}>
        {this.renderOverlayContents()}
      </FullScreenOverlay>
    );
  }
}

GroupSearchFeedback.propTypes = {
  groupSearchFeedbackActions: PropTypes.objectOf(PropTypes.func),
  sourceBib: ImmutablePropTypes.map,
  bibToMatch: ImmutablePropTypes.map,
  shouldMatch: PropTypes.bool,
  isEditing: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  searchResults: ImmutablePropTypes.list,
  isMissingRequiredFields: PropTypes.bool
};

export default GroupSearchFeedback;
